import { Stack, Text, Title, useMantineTheme } from '@mantine/core'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import React, { useCallback, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { NATIVE_CHAIN_LAUNCH_TICKER } from '@/constants/blockchain'
import useNetworkType from '@/hooks/useNetworkType'
import { useAuth } from '@/plugins/auth'
import { ProcessedBalance } from '@/utils/utils'

import RootButton from '../Buttons/RootButton'
import ChangeAddress from '../Modals/Airdrop/ChangeAddress'
import ModalSkeleton from '../Modals/ModalSkeleton'
import TooltipTitle from '../TooltipTitle'
import AddressDisplay from '../User/AddressDisplay'

interface OnChainRewardsProps {
  totalRewardAmount: ProcessedBalance
  nextStep: () => void
}

const OnChainRewards: React.FC<OnChainRewardsProps> = ({ totalRewardAmount, nextStep }) => {
  const theme = useMantineTheme()
  const { t } = useTranslation()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)
  const { address, disconnect } = useAuth()
  const [opened, { open, close }] = useDisclosure()
  const { isBetanet } = useNetworkType()

  // entered discord through change address flow
  const [enteredDiscord, setEnteredDiscord] = useState<boolean>(false)

  const goToMainnet = useCallback(() => {
    if (disconnect) {
      disconnect()
    }
    window.open('https://lamina1.com', '_target')
  }, [disconnect])

  return (
    <>
      <Stack style={{ gap: '30px' }}>
        <Title order={isMobile ? 3 : 2}>
          {t('components.airdrop.rewards', 'Congrats! Platform rewards are linked to your account')}
        </Title>
        <Text style={{ fontSize: 14 }}>
          {t(
            'components.airdrop.redeemLl1',
            'You’re eligible to claim the following Launch L1 (LL1) rewards tokens based on your activity in Mainnet.'
          )}
        </Text>
        <TooltipTitle
          title={t('components.airdrop.onChainRewards', 'Platform Rewards')}
          tooltip={t(
            'components.airdrop.onChainRewardsTooltip',
            `Platform rewards are linked to your LAMINA1 Hub and Telegram activity during the first 6 months of Mainnet. Relevant activity includes mainnet transactions, test platform transactions, items published, smart contracts deployed, and other factors. This amount also includes Telegram questing XP and any Rewards Pass multipliers applied to your account.`
          )}
        />
        <Stack
          style={{
            backgroundColor: theme.colors.background3[0],
            borderRadius: '12px',
            padding: '16px',
            width: 'fit-content',
            gap: 20,
          }}
        >
          <Title order={4}>
            {totalRewardAmount.display} {NATIVE_CHAIN_LAUNCH_TICKER}
          </Title>
        </Stack>
        <TooltipTitle
          title={t('components.airdrop.verifyAddress', 'Verify Your Account Address')}
          tooltip={
            !isBetanet ? (
              <Trans i18nKey="components.airdrop.verifyYourAddressTooltipBetanet">
                <Text style={{ color: theme.colors.title[0] }}>
                  Make sure this is the address you&apos;d like your rewards sent to before
                  continuing in the flow.
                </Text>
                <br />
                <Text style={{ color: theme.colors.title[0] }}>
                  If you have multiple LAMINA1 addresses, you can forward the account these rewards
                  are sent to by selecting &quot;Change Airdrop Address&quot; below.
                </Text>
                <br />
                <Text style={{ color: theme.colors.title[0] }}>
                  Otherwise, continue on by selecting &quot;Keep Address&quot; below.
                </Text>
              </Trans>
            ) : (
              <Trans i18nKey="components.airdrop.verifyAddressTooltip">
                <Text style={{ color: theme.colors.title[0] }}>
                  LAMINA1 community members can keep their address the same across Betanet and
                  Mainnet by signing in to <a href="https://lamina1.com">lamina1.com</a> with the
                  same email, social sign in, or wallet they used for their Betanet account.
                </Text>
                <br />
                <Text style={{ color: theme.colors.title[0] }}>
                  If these addresses match, your Airdrop rewards will be automatically forwarded to
                  your Mainnet account. If they don&apos;t, or if you have multiple Betanet accounts
                  you&apos;d like to forward to your new Mainnet account, you can transfer your
                  rewards by selecting &quot;Change Address&quot; below.
                </Text>
              </Trans>
            )
          }
        />
        <AddressDisplay
          showIdentity={false}
          address={address as string}
          truncated={isMobile}
          copyButton={false}
          tip={
            isBetanet
              ? t(
                  'components.airdrop.modal.confirmAddressTipBetanet',
                  'This is the address linked to your Betanet account'
                )
              : t(
                  'components.airdrop.modal.confirmAddressTip',
                  'This is the address linked to your Mainnet account'
                )
          }
        />

        <Stack style={{ flexDirection: 'row' }}>
          {!isBetanet ? (
            <Stack style={{ height: '170px', justifyContent: 'space-between' }}>
              <Stack style={{ gap: '0px' }}>
                <Title order={5}>
                  {t('components.airdrop.notPrimary', 'This is Not My Primary Account')}
                </Title>
                <Text style={{ fontSize: '14px' }}>
                  {t(
                    'components.airdrop.notPrimaryDescription',
                    'If you want to change the address your platform rewards are forwarded to, select this option.'
                  )}
                </Text>
              </Stack>
              <Stack style={{ flexDirection: 'row' }}>
                <RootButton secondary onClick={open} expand>
                  {t('buttons.changeAddress', 'Change Address')}
                </RootButton>
              </Stack>
            </Stack>
          ) : (
            <Stack style={{ height: '170px', justifyContent: 'space-between' }}>
              <Stack style={{ gap: '0px' }}>
                <Title order={5}>
                  {t('components.airdrop.betanetMatch', 'Betanet Matches Mainnet')}
                </Title>
                <Text style={{ fontSize: '14px' }}>
                  {t(
                    'components.airdrop.betanetMatchDescription',
                    'If this the same address you have on the LAMINA1 Mainnet and it’s your only Betanet address, claim your rewards by going to the Mainnet Hub.'
                  )}
                </Text>
              </Stack>
              <Stack style={{ flexDirection: 'row' }}>
                <RootButton onClick={goToMainnet} expand>
                  {t('buttons.goToMainnet', 'Go To Mainnet')}
                </RootButton>
              </Stack>
            </Stack>
          )}
          {!isBetanet ? (
            <Stack style={{ height: '170px', justifyContent: 'space-between' }}>
              <Stack style={{ gap: '0px' }}>
                <Title order={5}>
                  {t('components.airdrop.primary', 'This is My Primary Account')}
                </Title>
                <Text style={{ fontSize: '14px' }}>
                  {t(
                    'components.airdrop.primaryDescription',
                    'If this is the primary account with which you would like to claim your platform rewards, continue below.'
                  )}
                </Text>
              </Stack>
              <Stack style={{ flexDirection: 'row' }}>
                <RootButton onClick={nextStep} expand>
                  {t('buttons.keepAddress', 'Keep Address')}
                </RootButton>
              </Stack>
            </Stack>
          ) : (
            <Stack style={{ height: '170px', justifyContent: 'space-between' }}>
              <Stack style={{ gap: '0px' }}>
                <Title order={5}>
                  {t('components.airdrop.betanetDif', 'Betanet is Different to Mainnet')}
                </Title>
                <Text style={{ fontSize: '14px' }}>
                  {t(
                    'components.airdrop.betanetDifDescription',
                    'If this address is different to the one you have on Mainnet, or you’d like to change the account your rewards are forwarded to, select Change Address.'
                  )}
                </Text>
              </Stack>
              <Stack style={{ flexDirection: 'row' }}>
                <RootButton secondary onClick={open} expand>
                  {t('buttons.changeAddress', 'Change Address')}
                </RootButton>
              </Stack>
            </Stack>
          )}
        </Stack>
      </Stack>
      <ModalSkeleton
        size={isMobile ? 'md' : '550px'}
        opened={opened}
        onClose={close}
        title={t('components.airdrop.changeAddress', 'Change Airdrop Address')}
      >
        <ChangeAddress enteredDiscord={enteredDiscord} setEnteredDiscord={setEnteredDiscord} />
      </ModalSkeleton>
    </>
  )
}

export default OnChainRewards
